/**
 * EmptyState Component
 * Consistent empty state with icon, title, description, and optional action
 */
import { cn, Button } from "@jourfx/ui";
import { LucideIcon } from "lucide-react";

interface EmptyStateProps {
  /** Icon to display */
  icon: LucideIcon;
  /** Main title text */
  title: string;
  /** Optional description text */
  description?: string;
  /** Optional action button */
  action?: {
    label: string;
    onClick: () => void;
    icon?: LucideIcon;
  };
  /** Size variant */
  size?: "sm" | "md" | "lg";
  className?: string;
}

export function EmptyState({
  icon: Icon,
  title,
  description,
  action,
  size = "md",
  className,
}: EmptyStateProps) {
  const sizeStyles = {
    sm: {
      container: "p-6",
      icon: "h-8 w-8",
      title: "text-base",
      description: "text-sm",
    },
    md: {
      container: "p-8",
      icon: "h-12 w-12",
      title: "text-lg",
      description: "text-sm",
    },
    lg: {
      container: "p-12",
      icon: "h-16 w-16",
      title: "text-xl",
      description: "text-base",
    },
  };

  const styles = sizeStyles[size];

  return (
    <div
      className={cn(
        "rounded-lg border border-dashed bg-muted/30 text-center",
        styles.container,
        className
      )}
    >
      <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-muted">
        <Icon className={cn("text-muted-foreground", styles.icon)} />
      </div>
      <h3 className={cn("mt-4 font-medium text-foreground", styles.title)}>
        {title}
      </h3>
      {description && (
        <p className={cn("mt-2 text-muted-foreground", styles.description)}>
          {description}
        </p>
      )}
      {action && (
        <div className="mt-6">
          <Button onClick={action.onClick}>
            {action.icon && <action.icon className="mr-2 h-4 w-4" />}
            {action.label}
          </Button>
        </div>
      )}
    </div>
  );
}
